import { queryOptions } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import { MEDIA_GUIDELINE_QUERY_KEYS } from './query-keys';
import {
  guidelineCategoriesSchema,
  guidelineHistorySchema,
  guidelineListSchema,
  mediaGuidelineSchema,
  planRulesSchema,
} from './schemas';

/**
 * Indexed so it can be passed straight to the query key and the request's `query`
 * bag, both of which take a `Record<string, unknown>`.
 */
export interface MediaGuidelinesListFilters extends Record<string, unknown> {
  page?: number;
  pageSize?: number;
  search?: string;
  platform?: string[];
  campaignCategory?: string[];
  status?: string[];
}

export const mediaGuidelinesQueryOptions = (filters: MediaGuidelinesListFilters = {}) =>
  queryOptions({
    queryKey: MEDIA_GUIDELINE_QUERY_KEYS.list(filters),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-guidelines',
        query: filters,
        signal,
      });

      return parseResponse(guidelineListSchema, res);
    },
  });

export const mediaGuidelineQueryOptions = (id: string) =>
  queryOptions({
    queryKey: MEDIA_GUIDELINE_QUERY_KEYS.detail(id),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-guidelines/{id}',
        path: { id },
        signal,
      });

      return parseResponse(mediaGuidelineSchema, res);
    },
  });

export const mediaGuidelineHistoryQueryOptions = (id: string) =>
  queryOptions({
    queryKey: MEDIA_GUIDELINE_QUERY_KEYS.history(id),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-guidelines/{id}/history',
        path: { id },
        signal,
      });

      return parseResponse(guidelineHistorySchema, res);
    },
  });

/**
 * Campaign categories already used by guidelines in this workspace.
 *
 * The vocabulary is deliberately not an enum: it comes from what people have
 * authored, so the job form can offer real values instead of a free-text box that
 * invites the typo that would silently swap the rule set.
 */
export const guidelineCategoriesQueryOptions = () =>
  queryOptions({
    queryKey: MEDIA_GUIDELINE_QUERY_KEYS.categories(),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-guidelines/categories',
        signal,
      });

      return parseResponse(guidelineCategoriesSchema, res);
    },
  });

/** One check pre-flight runs before any house standard is written. */
export interface PlatformPlanRule {
  id: string;
  level: string;
  severity: 'error' | 'warning' | 'advice';
  describe: string;
  /** False for a rule true of every platform, rather than of this one. */
  platformSpecific: boolean;
}

export interface PlatformPlanRules {
  platform: string;
  label: string;
  rules: PlatformPlanRule[];
}

/**
 * The rules the platforms themselves impose, which pre-flight enforces whether or not
 * a workspace has written a single guideline.
 *
 * Read-only, and fetched separately from the guidelines list because it is a different
 * kind of thing: a house standard is a choice this workspace made, and these are
 * facts about what the platform will reject. They were previously legible only by
 * reading the source, which left "why did it flag that" with no answer.
 *
 * Effectively static — it changes when the app ships, not when anyone edits anything.
 */
export const platformPlanRulesQueryOptions = () =>
  queryOptions({
    queryKey: MEDIA_GUIDELINE_QUERY_KEYS.platformRules(),
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/plan-rules',
        signal,
      });
      return parseResponse(planRulesSchema, res).platforms;
    },
  });
